/**
 * Signals API Service
 *
 * Single integration point for the gTrade auto-trading backend (the
 * Railway "signal-engine" service). The browser only ever talks to this
 * backend over HTTPS — it NEVER holds keys and NEVER signs transactions.
 * All on-chain signing (setTradingDelegate / removeTradingDelegate / USDC
 * approve) happens in the student's own browser wallet via viem; see
 * SignalsTab.js. This file is strictly the REST client.
 *
 * ──────────────────────────────────────────────────────────────────────
 * HARD GATES (see SIGNALS_TAB_BRIEF.md):
 *   1. LEGAL  — no student's real funds execute live until Gven's lawyer
 *      clears the model. The tab ships behind a feature flag; this client
 *      will happily talk to a staging backend against Gven's own wallet.
 *   2. COMPLIANCE — education-first. This file returns backend data as-is
 *      and fabricates NOTHING. There is intentionally no mock-data mode:
 *      if the backend is unreachable we surface an honest error so the UI
 *      can show "not connected" rather than invent positions or balances.
 * ──────────────────────────────────────────────────────────────────────
 *
 * API contract (backend implements, this client consumes — do NOT diverge):
 *   GET  /delegate-address → { delegateAddress, diamond, chainId, usdc, usdcCollateralIndex }
 *   GET  /enroll/nonce?wallet= → { nonce, message, expiresAtMs }
 *                               // SIWE step 1 (LIVE contract, confirmed
 *                               // 2026-08-13): server-built message the wallet
 *                               // personal_signs VERBATIM. Free, off-chain,
 *                               // grants nothing. The ONLY enroll entry point.
 *   POST /enroll  { wallet, signature, nonce }
 *                          → { userId, webhookToken, webhookUrl, messageTemplate }
 *                               // SIWE step 2. 400 = missing/invalid signature
 *                               // or expired nonce, 409 = wallet already
 *                               // enrolled to an account.
 *                               // NOTE: a bare POST /enroll { wallet } gets a
 *                               // 400 — it is NOT an already-enrolled shortcut.
 *                               // The pre-SIWE challenge scaffolding
 *                               // (POST /enroll/challenge → challengeId → 428)
 *                               // was removed 2026-08-13; the deployed engine
 *                               // never spoke it. Do not reintroduce either.
 *   GET  /wallets           → { wallets:[{ address, verifiedAt, chainId, label,
 *                               isPrimary, automatedPairs:[] }] }
 *   POST /wallets  { wallet, signature, nonce, label? } → { ok, created, wallets }
 *                               // the multi-wallet registry (ARC-41). Same SIWE
 *                               // proof as /enroll — one nonce path, one
 *                               // verification. 401 = proof failed or nonce
 *                               // spent, 409 = wallet on another account.
 *   PATCH  /wallets/:address { label } → { ok, wallets }   // cosmetic
 *   DELETE /wallets/:address           → { ok, wallets }
 *                               // 409 names the automated pairs to deallocate
 *                               // first, or refuses the enrolled primary wallet
 *   GET  /status            → { wallet, delegationOk, collateralUsdc, enabled,
 *                               riskConfig (incl. allocations), pairMeta:{...},
 *                               openPositions:[...], recentSignals:[...] }
 *   POST /config  { maxLeverage, maxCollateralPctPerTrade, pairAllowlist:[], enabled,
 *                   allocations:{ "BTCUSD":{pct,leverage?}, ... } } → { ok }
 *                               // all fields optional (partial patch); allocations (#9)
 *                               // 2026-08-05 amendment: also accepts defaultLeverage
 *                               // and sizingMode ("allocation" | "fixed"), and now
 *                               // REJECTS unknown keys with a 400 naming them
 *                               // (see err.body). Use postConfig() for partial
 *                               // patches — setConfig() injects a pairAllowlist
 *                               // default that clears the allowlist.
 *   GET  /report            → { positions:[...], ledger:{...}, realizedPnlUsd,
 *                               totalPnlUsd, roi:{simplePct,moneyWeightedAnnualizedPct,
 *                               timeWeightedPct}, snapshot:{timestampMs,equityUsd,
 *                               netExternalFlowSinceLastUsd}, ledgerComplete,
 *                               recentEvents:[...], monthlyReturns:{...} }
 *                               // full shape at getReport()
 *   GET  /report/history    → { wallet, points:[{timestampMs,equityUsd,
 *                               netExternalFlowSinceLastUsd}], monthlyReturns }
 *                               // full shape at getReportHistory()
 *
 * Auth: every call carries the student's Firebase ID token as a Bearer
 * header. The backend verifies it with Firebase Admin (same
 * FIREBASE_SERVICE_ACCOUNT pattern as arc-research-proxy).
 *
 * Configuration:
 *   REACT_APP_SIGNALS_API_URL — base URL of the Railway backend.
 *     If unset, isConfigured() returns false and the tab shows a
 *     "coming soon / backend pending" state instead of calling out.
 */

import { getAuth } from "firebase/auth";

const SIGNALS_BASE = process.env.REACT_APP_SIGNALS_API_URL || "";

const HAS_BASE = Boolean(SIGNALS_BASE && SIGNALS_BASE.startsWith("http"));

/* ─── Auth helper ──────────────────────────────────────────────────────── */
// Pull a fresh Firebase ID token for the Bearer header. Returns null if no
// user is signed in (the tab guards on auth before calling, but we fail
// safe here too).
async function getIdToken() {
  const user = getAuth().currentUser;
  if (!user) return null;
  try {
    return await user.getIdToken();
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error("[signalsApi] failed to mint Firebase ID token:", e);
    return null;
  }
}

/* ─── Core fetch wrapper ───────────────────────────────────────────────── */
async function apiFetch(path, { method = "GET", body } = {}) {
  if (!HAS_BASE) {
    const err = new Error(
      "Strategy Automation backend is not configured yet. Set REACT_APP_SIGNALS_API_URL."
    );
    err.code = "NOT_CONFIGURED";
    throw err;
  }

  const token = await getIdToken();
  if (!token) {
    const err = new Error("You must be signed in to use Strategy Automation.");
    err.code = "NO_AUTH";
    throw err;
  }

  const headers = {
    Accept: "application/json",
    Authorization: `Bearer ${token}`,
  };
  if (body !== undefined) headers["Content-Type"] = "application/json";

  let res;
  try {
    res = await fetch(`${SIGNALS_BASE}${path}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (networkErr) {
    const err = new Error(
      "Couldn't reach the Strategy Automation backend. Check your connection and try again."
    );
    err.code = "NETWORK";
    err.cause = networkErr;
    throw err;
  }

  if (res.status === 401 || res.status === 403) {
    const err = new Error(
      "Your session isn't authorized for Strategy Automation. Try signing out and back in."
    );
    err.code = "AUTH";
    // The wallet registry (ARC-46) answers a failed proof AND a spent nonce with
    // 401, and only the engine's own sentence tells them apart — "sign with the
    // right wallet" vs "start again for a fresh request". Carrying the status
    // and the JSON body lets callers classify; the code and message are
    // unchanged, so an expired Firebase session still reads as AUTH.
    err.status = res.status;
    const text = await res.text().catch(() => "");
    try {
      const parsed = JSON.parse(text);
      if (parsed && typeof parsed === "object") err.body = parsed;
    } catch {
      /* non-JSON body — nothing more to say than the status */
    }
    throw err;
  }

  if (res.status === 428) {
    // Vestigial: the live engine does not send 428 on any endpoint (enrollment
    // signals a missing signature with 400). Kept only as a generic guard so an
    // unexpected 428 surfaces a readable message instead of a bare status. It is
    // NOT an enrollment code path — nothing branches on it any more.
    const err = new Error("Wallet-ownership signature required to enroll.");
    err.code = "SIGNATURE_REQUIRED";
    throw err;
  }

  if (res.status === 429) {
    const err = new Error("Too many requests. Give it a minute and try again.");
    err.code = "RATE_LIMIT";
    throw err;
  }

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    const err = new Error(
      `Signals API error: ${res.status} ${res.statusText} ${text.slice(0, 200)}`.trim()
    );
    err.code = "HTTP_" + res.status;
    err.status = res.status;
    // Structured error body when the backend sends JSON. POST /config names the
    // rejected keys on a 400 under the 2026-08-05 contract; callers read
    // err.body rather than regex-ing the truncated message string.
    try {
      const parsed = JSON.parse(text);
      if (parsed && typeof parsed === "object") err.body = parsed;
    } catch {
      /* non-JSON error body — err.message still carries the raw text */
    }
    throw err;
  }

  // 204 / empty-body tolerance
  if (res.status === 204) return {};
  const text = await res.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    return {};
  }
}

/* ─── Public: endpoints (the whole contract, nothing more) ─────────────── */

/**
 * GET /delegate-address
 * → { delegateAddress, diamond, chainId, usdc, usdcCollateralIndex }
 * The on-chain constants the tab needs to build the delegate + approve txs.
 * Safe to call before enrollment (no body, just the Bearer token).
 */
export async function getDelegateAddress() {
  return apiFetch("/delegate-address");
}

/*
 * REMOVED 2026-08-13: getEnrollChallenge() (POST /enroll/challenge) and the
 * bare/probe enroll(wallet, proof?) (POST /enroll { wallet } → 428). Both were
 * stale scaffolding the deployed engine never spoke; the probe made every
 * member's enrollment fail with a raw 400. The live flow is getEnrollNonce() +
 * enrollWithSignature() below — do not reintroduce a bare enroll.
 */

/**
 * GET /enroll/nonce?wallet=0x… → { nonce, message, expiresAtMs }
 * SIWE step 1 of the LIVE enrollment contract (confirmed 2026-08-13). The
 * server builds the exact message the wallet must sign, bound to the wallet
 * passed in the query. The client NEVER authors or reformats that message — a
 * client-rebuilt SIWE message is how replay and phishing bugs get in.
 *
 * Detected by existence (components/strategyautomation/useEnrollment.js) — do
 * not rename.
 */
export async function getEnrollNonce(wallet) {
  if (!wallet) throw new Error("A connected wallet address is required.");
  return apiFetch(`/enroll/nonce?wallet=${encodeURIComponent(wallet)}`);
}

/**
 * POST /enroll { wallet, signature, nonce }
 * → { userId, webhookToken, webhookUrl, messageTemplate }
 * SIWE step 2. Enrollment is CLOSED without this: the pre-SIWE single-call
 * enroll({ wallet }) returns 400.
 * Errors: 400 missing/invalid signature or expired nonce; 409 the wallet is
 * already enrolled to an account (never auto-transferred — support only).
 *
 * This is the ONLY enroll call in the client. The legacy challenge/bare-enroll
 * scaffolding was deleted 2026-08-13 once SignalsTab.js migrated onto the nonce
 * flow — do not add a signature-less variant back.
 *
 * Detected by existence — do not rename.
 */
export async function enrollWithSignature({ wallet, signature, nonce }) {
  if (!wallet) throw new Error("A connected wallet address is required to enroll.");
  return apiFetch("/enroll", { method: "POST", body: { wallet, signature, nonce } });
}

/**
 * GET /status
 * → { wallet, delegationOk, collateralUsdc, enabled,
 *     riskConfig: { maxLeverage, maxCollateralPctPerTrade, pairAllowlist,
 *                   enabled, allocations?:{ "BTCUSD":{pct,leverage?} } },
 *     pairMeta: { "BTCUSD": { pairIndex, groupName, minPositionSizeUsd,
 *                             minLeverage, maxLeverage } },
 *       // read-only gTrade limits echo for allowlisted pairs (#9) — used for
 *       // the below-minimum allocation warning; may be {} if the venue
 *       // metadata fetch failed (UI then skips the warning, invents nothing)
 *     openPositions:[{pair,long,sizeUsd,collateralUsd}],
 *     recentSignals:[{ts,pair,intent,status}] }
 */
export async function getStatus() {
  return apiFetch("/status");
}

/**
 * POST /config { maxLeverage, maxCollateralPctPerTrade, pairAllowlist:[], enabled,
 *                allocations }
 * → { ok }
 * Partial patch: omitted fields keep their server-side values (undefined keys
 * are dropped by JSON.stringify, so passing no `allocations` never clears them).
 */
export async function setConfig(config) {
  const {
    maxLeverage,
    maxCollateralPctPerTrade,
    pairAllowlist = [],
    enabled,
    allocations,
  } = config || {};
  return apiFetch("/config", {
    method: "POST",
    body: { maxLeverage, maxCollateralPctPerTrade, pairAllowlist, enabled, allocations },
  });
}

/**
 * POST /config { allocations } — allocation-only partial patch (#9).
 * → { ok }
 * allocations: { "BTCUSD": { pct, leverage? }, ... } keyed by the engine's
 * normalized pair name (uppercase, alphanumerics only). Server validation:
 * non-cash pcts must sum ≤ 100 (cash is the remainder), every key must already
 * be in the user's pairAllowlist, leverage is clamped to the 2x server ceiling,
 * pct 0 entries are dropped, and `{}` clears all allocations.
 * PERSISTENCE ONLY today: saved allocations do not size trades until the
 * engine's audit-gated ALLOCATION_SIZING_ENABLED flag is armed server-side.
 * The Strategy Automation tab detects allocation support by this function's
 * existence — do not rename it.
 */
export async function setAllocations(allocations) {
  return apiFetch("/config", { method: "POST", body: { allocations } });
}

/**
 * POST /config { ...partial } — generic partial patch (2026-08-05 contract).
 * → { ok }
 * Sends the caller's body VERBATIM. No defaults are injected, so an omitted key
 * keeps its server-side value and can never be cleared by accident — unlike
 * setConfig(), which defaults pairAllowlist to [] and would wipe a member's
 * allowlist on any patch that didn't restate it.
 * Accepted keys (anything else → 400 naming them in err.body):
 *   maxLeverage, maxCollateralPctPerTrade, pairAllowlist, enabled,
 *   allocations, defaultLeverage, sizingMode
 * This is what lets one save carry sizingMode ALONGSIDE allocations, which the
 * engine requires for the allocation/fixed sizing switch (a standalone removal
 * of the last allocation is rejected by design).
 * Callers must sanitize before calling; the Strategy Automation tab does it in
 * components/strategyautomation/configContract.js. Detected by existence — do
 * not rename.
 */
export async function postConfig(patch) {
  return apiFetch("/config", { method: "POST", body: patch || {} });
}

/**
 * GET /report — performance/reporting snapshot. READ-ONLY: moves no money,
 * changes no execution.
 * → { positions: [{ pairKey, long, leverage, collateralUsd, sizeUsd,
 *                    entryPriceUsd, currentPriceUsd, unrealizedPnlUsd }],
 *                    // currentPriceUsd/unrealizedPnlUsd null = unpriced
 *     ledger: { depositsUsd, withdrawalsUsd, tradingInUsd, tradingOutUsd,
 *               externalFlows:[{timestampMs,amountUsd}],
 *               externalCounterparties:{<address>:number},
 *               openingEquityUsd, openingTimestampMs },
 *     realizedPnlUsd, totalPnlUsd,
 *     roi: { simplePct, moneyWeightedAnnualizedPct, timeWeightedPct }, // any may be null
 *     snapshot: { timestampMs, equityUsd, netExternalFlowSinceLastUsd },
 *     ledgerComplete,
 *     recentEvents: [{ userId, wallet, atMs, at, pair, intent, status,
 *                      detail?, txHash?, priceUsd?, strategy? }],
 *     monthlyReturns: { ... } }  // same shape as getReportHistory() below
 * NOTE: carries only the single current snapshot — the equity time series
 * for the growth chart lives at GET /report/history (getReportHistory()).
 * Errors: 404 { error:"not enrolled" } before enrollment (callers treat as
 * "no report yet"); 500 { error:"report generation failed", detail }.
 */
export async function getReport() {
  return apiFetch("/report");
}

/**
 * GET /report/history — the equity time series behind the growth chart, plus
 * calendar-month returns. READ-ONLY: moves no money, changes no execution.
 * → { wallet,
 *     points: [{ timestampMs, equityUsd, netExternalFlowSinceLastUsd }],
 *       // oldest-first, one entry per stored valuation snapshot; may be
 *       // empty or short while history is still building — the UI keeps its
 *       // honest "building history" state rather than plotting a fabrication
 *     monthlyReturns: {
 *       months: [{ month: "2026-07", returnPct, partial, periods }],
 *         // oldest-first; months with no data are ABSENT, never fabricated
 *       averageMonthlyReturnPct, // mean over COMPLETED months only; null if none
 *       completedMonths,
 *       insufficientHistory      // true until ≥1 completed month
 *     } }
 * Errors: 401 bad token; 404 { error:"not enrolled" } before enrollment
 * (callers treat as "no history yet"); 500 { error, detail }.
 */
export async function getReportHistory() {
  return apiFetch("/report/history");
}

/* ─── Public: the wallet registry (ARC-41 engine, ARC-46 tab) ──────────────
 * A member may prove several wallets and segment strategies across them —
 * stake on one, trade from another — against a single member-level slot pool.
 *
 * Attaching reuses getEnrollNonce() above: ONE proof path for the whole app.
 * The wallet personal_signs the server's message verbatim; free, off-chain,
 * sends no transaction and grants no spending or trading permission.
 *
 * Detected by existence (components/strategyautomation/useWalletRegistry.js) —
 * do not rename.
 */

/**
 * GET /wallets
 * → { wallets: [{ address, verifiedAt, chainId, label, isPrimary,
 *                 automatedPairs: ["BTCUSD", …] }] }
 * Addresses come back checksummed. 404 { error: "not enrolled" } before the
 * first wallet exists — callers read that as "enroll first", not as an error.
 */
export async function listWallets() {
  return apiFetch("/wallets");
}

/**
 * POST /wallets { wallet, signature, nonce, label? }
 * → { ok, created, wallets: [...] }
 * Idempotent per address: re-proving a wallet already on the account returns
 * created:false and keeps its label unless a new one is supplied.
 * Errors: 400 malformed address/label; 401 the proof failed OR the nonce was
 * never outstanding (single-use — the two are told apart by the engine's own
 * error text, see walletRegistry.classifyWalletError); 403 the nonce was issued
 * to a different account; 404 not enrolled; 409 the wallet is already attached
 * to a DIFFERENT account (one wallet, one account — never auto-transferred).
 */
export async function attachWallet({ wallet, signature, nonce, label } = {}) {
  if (!wallet) throw new Error("A wallet address is required.");
  if (!signature) throw new Error("A wallet signature is required.");
  const body = { wallet, signature, nonce };
  // Only sent when the member typed one — an omitted label keeps the label the
  // wallet already had; an explicit null would clear it.
  if (label !== undefined) body.label = label;
  return apiFetch("/wallets", { method: "POST", body });
}

/**
 * PATCH /wallets/:address { label } → { ok, wallets: [...] }
 * Cosmetic rename. No signature: a label changes nothing the engine acts on.
 * Errors: 400 label too long; 404 no such wallet on this account.
 */
export async function setWalletLabel(address, label) {
  if (!address) throw new Error("A wallet address is required.");
  return apiFetch(`/wallets/${encodeURIComponent(address)}`, {
    method: "PATCH",
    body: { label },
  });
}

/**
 * DELETE /wallets/:address → { ok, wallets: [...] }
 * Errors: 404 no such wallet; 409 the wallet still has automated pairs (the
 * message names them and says to deallocate first), or it is the enrolled
 * primary wallet. Both 409 bodies are member-facing next steps and the tab
 * surfaces them rather than a generic failure.
 */
export async function detachWallet(address) {
  if (!address) throw new Error("A wallet address is required.");
  return apiFetch(`/wallets/${encodeURIComponent(address)}`, { method: "DELETE" });
}

/* ─── Public: status helpers ───────────────────────────────────────────── */
export const isConfigured = () => HAS_BASE;

export default {
  getDelegateAddress,
  getEnrollNonce,
  enrollWithSignature,
  listWallets,
  attachWallet,
  setWalletLabel,
  detachWallet,
  getStatus,
  setConfig,
  setAllocations,
  postConfig,
  getReport,
  getReportHistory,
  isConfigured,
};
